import type { Ruleset, Seat } from '@aod/protocol';
import { RIM_SEA } from '@aod/protocol';
import { type CitySuggestion, TickLoop, createRustSimAdapter } from '@aod/sim-adapter';
import { RustSimAdapter } from '@aod/sim-wasm';
import { captureError } from '../../observability.js';
import type { Archetype } from '../../ui/DoctrinePanel/index.js';
import { GameDomWsBridge } from '../wsBridge.js';
import { EventTagger } from './EventTagger.js';
import { loadOptionalConfig } from './loadOptionalConfig.js';
import { mapMinimap, mapPlayerHud, summarize } from './summarize.js';
import { PLACEHOLDER_SIM_WORLD, type SimWorld } from './types.js';
import { type DynastyActions, NO_DYNASTY } from './useDynastyIntents.js';
import { type EspionageActions, NO_ESPIONAGE } from './useEspionageIntents.js';
import { type HeroCaptivityActions, NO_HERO_CAPTIVITY } from './useHeroCaptivityIntents.js';
import { type HeroQuestActions, NO_HERO_QUESTS } from './useHeroQuestIntents.js';
import { NO_TREATIES, type TreatyActions } from './useTreatyIntents.js';

interface BootOptions {
  configJson?: string;
  /** Match ruleset from the setup screen; forwarded to the wasm options. */
  ruleset?: Ruleset;
  /** Authored `aod-map` document (JSON) from the World tab; forwarded. */
  mapJson?: string;
  /** Seats (seat 0 = local human); forwarded to the wasm options. */
  seats?: readonly Seat[];
  /** Team of each seat (multiplayer teams); forwarded as `teams`. */
  teams?: readonly number[];
  matchSeed: number;
  tickRateHz: number;
  localPlayerIndex: number;
  invest: (archetype: Archetype) => void;
  attackCity: (targetCityId: string) => void;
  moveUnit: (unitId: string, targetQ: number, targetR: number) => boolean;
  buildBridge: (unitId: string, targetQ: number, targetR: number) => boolean;
  sailShip: (unitId: string, targetQ: number, targetR: number) => boolean;
  dockTrade: (unitId: string, cityId: string, food?: string) => boolean;
  acceptMerchant: (cityId: string) => boolean;
  declineMerchant: (cityId: string) => boolean;
  dynasty: DynastyActions;
  espionage: EspionageActions;
  heroCaptivity: HeroCaptivityActions;
  heroQuests: HeroQuestActions;
  treaty: TreatyActions;
  deployUnit: (unitId: string, targetQ: number, targetR: number) => boolean;
  setFormation: (unitId: string, formation: string) => void;
  startResearch: () => void;
  pilgrimage: () => void;
  reform: () => void;
  startBuilding: (cityId: string, buildingId: string) => boolean;
  setCityDoctrine: (cityId: string, archetype: Archetype) => boolean;
  /** Spec §8 suggest mode — see SimWorld.suggestIntents. */
  suggestIntents: () => readonly CitySuggestion[];
  summonLegend: (cityId: string) => string | null;
  activateLegendAbility: (
    unitId: string,
    slot: number,
    targetQ: number,
    targetR: number,
  ) => string | null;
  activateHeroAbility: (unitId: string, slotIndex: number) => void;
  selectHeroAbility: (unitId: string, slotIndex: number, abilityId: string) => void;
  pause: () => void;
  resume: () => void;
  setSpeed: (multiplier: number) => void;
  fastForward: (ticks: number) => void;
  newMatch: (newSeed?: number) => void;
  saveGame: () => string;
  loadGame: (json: string) => void;
  /** When true, skip TickLoop creation. Caller drives steps via externalStep(). */
  externalTick?: boolean;
  setState: (updater: SimWorld | ((prev: SimWorld) => SimWorld)) => void;
}

interface BootRefs {
  adapter: { current: ReturnType<typeof createRustSimAdapter> | null };
  ws: { current: GameDomWsBridge | null };
  loop: { current: TickLoop | null };
  publish: { current: (() => void) | null };
  events: { current: Array<{ id: number; type: string; payload?: Record<string, unknown> }> };
  unsub: { current: (() => void) | null };
}

export async function bootSim(
  opts: BootOptions,
  refs: BootRefs,
): Promise<{ cleanup: () => void; externalStep?: (tick: number) => void }> {
  let cancelled = false;

  try {
    // `@aod/sim-wasm` is built with `wasm-pack --target bundler`, so the
    // entry module's import-time side effect already instantiates the wasm
    // module and wires it into `RustSimAdapter`'s FFI shim. Nothing to await
    // here — the import below is what triggers it.

    let configJson = opts.configJson;
    if (!configJson) {
      // Only attempt the fetch when an explicit URL is configured — a fetch
      // to a closed port logs `ERR_CONNECTION_REFUSED` to the browser
      // console regardless of try/catch. Skipping the call entirely when
      // no URL is set keeps dev/audit consoles clean.
      configJson = await loadOptionalConfig(import.meta.env.VITE_CONFIG_URL);
    }

    const adapter = createRustSimAdapter(RustSimAdapter as never, {
      appId: 'ages-of-dominion',
      matchSeed: opts.matchSeed,
      ...(configJson ? { configJson } : {}),
      ...(opts.ruleset ? { ruleset: opts.ruleset } : {}),
      ...(opts.seats ? { seats: opts.seats } : {}),
      ...(opts.teams ? { teams: opts.teams } : {}),
      ...(opts.mapJson ? { map: opts.mapJson } : {}),
    });
    refs.adapter.current = adapter;

    // Tag every incoming sim event with a monotonic id so consumers can
    // key by something stable. (Audit 2026-07-16 finding B3.)
    const eventTagger = new EventTagger();
    // Event-history cap — an unbounded array at ~40 publishes/s (4×) leaks
    // and O(n²)-copies itself until the renderer OOMs (HANDOVER "Known
    // crash"). Consumers key by the monotonic event id, so dropping the
    // oldest is safe.
    const MAX_EVENTS = 400;
    const unsub = adapter.subscribe('*', (event) => {
      // Bounded buffer — at 4× (~40 publishes/s) an unbounded array both
      // leaks and re-copies itself O(n²) until the renderer OOMs (see
      // HANDOVER "Known crash"). Consumers key by the monotonic event id,
      // so dropping the oldest is safe.
      const tagged = eventTagger.tag({
        type: event.type,
        payload: event.payload as Record<string, unknown> | undefined,
      });
      const list = refs.events.current;
      list.push(tagged);
      if (list.length > MAX_EVENTS) list.shift();
    });
    refs.unsub.current = unsub;

    // Only open a WebSocket when an explicit game-dom-ws URL is configured.
    // The localhost fallback used to fire a `ws://localhost:3163/` connect
    // every ~6s in single-player audits (regression observed 2026-07-16:
    // 7 console errors in a 60s session). Nothing on the client reads these
    // snapshots — the bridge is reserved for a future multiplayer gateway —
    // so defaulting to "off" keeps the console clean in dev/preview/CI.
    const wsUrl = import.meta.env.VITE_GAME_DOM_WS_URL;
    const ws = wsUrl
      ? new GameDomWsBridge({
          url: wsUrl,
          appId: 'ages-of-dominion',
          onState: (s) =>
            opts.setState((prev) => (prev.status === 'ready' ? { ...prev, wsState: s } : prev)),
        })
      : null;
    ws?.connect();
    refs.ws.current = ws;

    const localIdx = opts.localPlayerIndex;
    const publish = () => {
      const snapshot = adapter.snapshot();
      ws?.publishSnapshot(snapshot);
      const summary = summarize(snapshot, localIdx);

      // Server-authoritative minimap and player HUD (fog of war, diplomacy info-gating).
      const minimapRaw = adapter.minimapSnapshot(localIdx) as Record<string, unknown> | null;
      const playerHudRaw = adapter.playerHud(localIdx) as Record<string, unknown>[] | null;
      const minimapSnapshot = minimapRaw
        ? mapMinimap(minimapRaw as unknown as Parameters<typeof mapMinimap>[0], summary.sea)
        : null;
      const playerHudEntries = playerHudRaw
        ? mapPlayerHud(playerHudRaw as unknown as Parameters<typeof mapPlayerHud>[0])
        : [];

      const loop = refs.loop.current;
      if (summary.victory && refs.loop.current?.getRunState() === 'running') {
        refs.loop.current.pause();
      }
      // externalStep / applyFrame are installed once the boot resolves and are
      // how a multiplayer client advances; publish() runs after EVERY step, so
      // it must carry them forward. It used to reset them to no-ops, which froze
      // the client sim after its first frame (found with two real browsers).
      opts.setState((prev) => ({
        status: 'ready',
        snapshot,
        tick: adapter.tick(),
        determinismHash: adapter.determinismHash(),
        tileCount: summary.tileCount,
        cityCount: summary.cityCount,
        localPlayer: summary.localPlayer,
        localTreasury: summary.localTreasury,
        playerHudEntries,
        minimapSnapshot,
        wsState: ws?.state ?? 'closed',
        // Fresh reference per publish so React memo deps see the change —
        // the buffer itself is mutated in place (bounded above).
        events: [...refs.events.current],
        invest: opts.invest,
        runState: loop?.getRunState() ?? 'stopped',
        speed: loop?.getSpeed() ?? 1,
        pause: opts.pause,
        resume: opts.resume,
        setSpeed: opts.setSpeed,
        fastForward: opts.fastForward,
        victory: summary.victory,
        victoryProgress: summary.victoryProgress,
        climate: summary.climate,
        sea: summary.sea,
        attackCity: opts.attackCity,
        newMatch: opts.newMatch,
        moveUnit: opts.moveUnit,
        buildBridge: opts.buildBridge ?? (() => false),
        sailShip: opts.sailShip,
        dockTrade: opts.dockTrade,
        acceptMerchant: opts.acceptMerchant,
        declineMerchant: opts.declineMerchant,
        dynasty: opts.dynasty,
        espionage: opts.espionage,
        heroCaptivity: opts.heroCaptivity,
        heroQuests: opts.heroQuests,
        treaty: opts.treaty,
        deployUnit: opts.deployUnit,
        setFormation: opts.setFormation,
        startResearch: opts.startResearch,
        researchState: summary.researchState,
        faithCrisis: summary.faithCrisis,
        pilgrimage: opts.pilgrimage,
        reform: opts.reform,
        startBuilding: opts.startBuilding,
        setCityDoctrine: opts.setCityDoctrine,
        suggestIntents: opts.suggestIntents,
        summonLegend: opts.summonLegend,
        activateLegendAbility: opts.activateLegendAbility,
        activateHeroAbility: opts.activateHeroAbility,
        selectHeroAbility: opts.selectHeroAbility,
        saveGame: opts.saveGame,
        loadGame: opts.loadGame,
        externalStep: prev.externalStep,
        applyFrame: prev.applyFrame,
        getDeterminismHash: () => adapter.determinismHash(),
      }));
    };
    refs.publish.current = publish;

    if (!opts.externalTick) {
      const loop = new TickLoop({
        tickRateHz: opts.tickRateHz,
        step: (n) => adapter.step(n),
        onTick: () => publish(),
      });
      loop.start();
      refs.loop.current = loop;
    }

    publish(); // initial render
  } catch (err) {
    if (!cancelled) {
      // Observability is a no-op without SENTRY_DSN — captures the failure
      // (e.g., broken WASM init) with phase context for triage.
      captureError(err, { phase: 'bootSim' });
      // eslint-disable-next-line no-console
      console.error('[bootSim] failed:', err);
      // Inline the placeholder defaults rather than spreading
      // PLACEHOLDER_SIM_WORLD. Module-init ordering can leave the imported
      // const in a TDZ during error paths (the audit surfaced this when a
      // broken WASM init threw inside this catch and the spread of the
      // half-evaluated placeholder threw again, masking the real cause).
      opts.setState({
        status: 'error',
        error: err instanceof Error ? err.message : String(err),
        snapshot: null,
        tick: 0n,
        tileCount: 0,
        cityCount: 0,
        determinismHash: 0n,
        wsState: 'closed',
        localPlayer: null,
        localTreasury: { food: 0, industry: 0, knowledge: 0, faith: 0, gold: 0 },
        playerHudEntries: [],
        minimapSnapshot: null,
        invest: () => {},
        runState: 'stopped',
        speed: 1,
        pause: () => {},
        resume: () => {},
        setSpeed: () => {},
        fastForward: () => {},
        victory: null,
        victoryProgress: null,
        climate: 'temperate',
        sea: RIM_SEA,
        attackCity: () => {},
        newMatch: () => {},
        moveUnit: () => false,
        buildBridge: () => false,
        sailShip: () => false,
        dockTrade: () => false,
        acceptMerchant: () => false,
        declineMerchant: () => false,
        dynasty: NO_DYNASTY,
        espionage: NO_ESPIONAGE,
        heroCaptivity: NO_HERO_CAPTIVITY,
        heroQuests: NO_HERO_QUESTS,
        treaty: NO_TREATIES,
        deployUnit: () => false,
        setFormation: () => {},
        startResearch: () => {},
        researchState: null,
        faithCrisis: null,
        pilgrimage: () => {},
        reform: () => {},
        startBuilding: () => false,
        setCityDoctrine: () => false,
        suggestIntents: () => [],
        summonLegend: () => 'unavailable',
        activateLegendAbility: () => 'unavailable',
        activateHeroAbility: () => {},
        selectHeroAbility: () => {},
        events: [],
        saveGame: () => '',
        loadGame: () => {},
        externalStep: () => {},
        applyFrame: () => {},
        getDeterminismHash: () => 0n,
      });
    }
  }

  const cleanup = () => {
    cancelled = true;
    refs.unsub.current?.();
    refs.loop.current?.stop();
    refs.ws.current?.disconnect();
    refs.adapter.current?.destroy();
    refs.adapter.current = null;
    refs.events.current = [];
  };

  if (opts.externalTick) {
    const externalStep = (tick: number) => {
      const adapter = refs.adapter.current;
      const publish = refs.publish.current;
      if (!adapter || !publish) return;
      adapter.step(tick);
      publish();
    };
    return { cleanup, externalStep };
  }

  return { cleanup };
}
